import { useState } from 'react';
import { ApiError } from '@/services/api/client';
import {
  useBeginMfaEnrollment,
  useConfirmMfaEnrollment,
  useDisableMfa,
  useMfaStatus,
} from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { StatusPill } from '@/shared/ui/status-pill';
import { AuthenticatorSetup } from '../auth/AuthenticatorSetup';
import { SettingsGroup, SettingsRow } from './SettingsGroup';

/**
 * Two-factor authentication for whoever is signed in.
 *
 * Enrolment is two steps on purpose: the secret is generated first and only
 * takes effect once a code proves it reached the phone. Activating on
 * generation would lock the account behind a device that may never have
 * scanned it. Turning it off needs a current code too - otherwise a stolen
 * session could quietly remove the control that would have stopped it.
 */
export function SecuritySettings() {
  const status = useMfaStatus();
  const begin = useBeginMfaEnrollment();
  const confirm = useConfirmMfaEnrollment();
  const disable = useDisableMfa();

  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  const enabled = status.data?.enabled ?? false;
  const secret = begin.data;
  const errorOf = (error: unknown) => (error instanceof ApiError ? error.message : null);

  const control = status.isPending ? (
    <Skeleton className="h-8 w-28" />
  ) : enabled ? (
    <>
      <StatusPill status="active" tone="positive">
        On
      </StatusPill>
      {!disabling && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setDisabling(true);
          }}
        >
          Turn off
        </Button>
      )}
    </>
  ) : (
    <>
      <StatusPill status="off" tone="inert">
        Off
      </StatusPill>
      {!secret && (
        <Button
          size="sm"
          disabled={begin.isPending}
          onClick={() => {
            begin.mutate();
          }}
        >
          {begin.isPending ? 'Setting up…' : 'Set up'}
        </Button>
      )}
    </>
  );

  const codeInput = (id: string, value: string, onChange: (value: string) => void) => (
    <Input
      id={id}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      placeholder="000000"
      aria-label="6-digit code"
      className="max-w-32 text-center font-mono tracking-widest"
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );

  return (
    <SettingsGroup title="Security" sub="Your own account on the operations portal">
      <SettingsRow
        label="Two-factor authentication"
        desc={
          enabled && status.data?.enrolledAt
            ? `A code from your phone as well as your password. On since ${new Date(
                status.data.enrolledAt,
              ).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
            : 'A code from your phone as well as your password. Works with any authenticator app.'
        }
        control={control}
      >
        {errorOf(begin.error) && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {/* Most likely a server with no MFA_ENCRYPTION_KEY, which refuses
                to store a TOTP secret unprotected. */}
            {errorOf(begin.error)}
          </p>
        )}

        {!enabled && secret && (
          <div className="mt-4 max-w-sm space-y-4">
            <AuthenticatorSetup
              secret={secret.secret}
              otpauthUrl={secret.otpauthUrl}
              lostSecretHint="Shown once. If you lose it before confirming, start the setup again."
            />
            <p className="text-xs text-muted-foreground">
              Nothing changes until you confirm with a code, so scanning it wrong cannot lock you
              out.
            </p>
            {errorOf(confirm.error) && (
              <p role="alert" className="text-sm text-destructive">
                {errorOf(confirm.error)}
              </p>
            )}
            <div className="flex gap-2">
              {codeInput('confirm-code', code, setCode)}
              <Button
                disabled={code.trim().length !== 6 || confirm.isPending}
                onClick={() => {
                  confirm.mutate(code.trim(), {
                    onSuccess: () => {
                      setCode('');
                      begin.reset();
                    },
                  });
                }}
              >
                {confirm.isPending ? 'Confirming…' : 'Turn on'}
              </Button>
            </div>
          </div>
        )}

        {enabled && disabling && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-muted-foreground">
              Enter a current code to confirm it is you. Being signed in is not enough - that is the
              point of the second factor.
            </p>
            {errorOf(disable.error) && (
              <p role="alert" className="text-sm text-destructive">
                {errorOf(disable.error)}
              </p>
            )}
            <div className="flex gap-2">
              {codeInput('disable-code', disableCode, setDisableCode)}
              <Button
                variant="outline"
                disabled={disableCode.trim().length !== 6 || disable.isPending}
                onClick={() => {
                  disable.mutate(disableCode.trim(), {
                    onSuccess: () => {
                      setDisableCode('');
                      setDisabling(false);
                    },
                  });
                }}
              >
                {disable.isPending ? 'Turning off…' : 'Turn off'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDisabling(false);
                  setDisableCode('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </SettingsRow>
    </SettingsGroup>
  );
}
